import { ReactNode } from 'react';
import { Box, Container, Grid, Typography, alpha, Stack, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { SafetyOutlined, BankOutlined, TeamOutlined, ArrowRightOutlined, AuditOutlined } from '@ant-design/icons';
import { SECTION_PY } from 'const/section';

const BillingSupportSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const primaryColor = '#2aa1af';

    // Hero(LandingTop)のリード文にある「個別支援計画書」と同じマーカー風の強調効果
    const Highlight = ({ children }: { children: ReactNode }) => (
        <Box
            component="span"
            sx={{
                position: 'relative',
                fontWeight: 700,
                display: 'inline-block',
                mx: 0.5,
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    bottom: 1,
                    width: '100%',
                    height: '5px',
                    background: alpha(primaryColor, 0.2),
                    borderRadius: '4px',
                    filter: 'blur(2px)',
                    zIndex: -1
                }
            }}
        >
            {children}
        </Box>
    );

    const billingData: { title: string; desc: ReactNode; icon: JSX.Element }[] = [
        {
            title: '不登校支援加算(個別サポート加算Ⅲ)',
            desc: (
                <>
                    日々の様子を<Highlight>音声入力</Highlight>するだけで、学校提出用の報告書をAIが自動で清書。加算算定に必要な書類も無理なく揃います。
                </>
            ),
            icon: <SafetyOutlined />,
        },
        {
            title: '専門的支援実施加算の取得',
            desc: 'アセスメント結果をもとに、算定に必要な「専門的支援実施計画書」をAIが自動生成。専門性の高い支援を、そのまま収益につなげます。',
            icon: <BankOutlined />,
        },
        {
            title: '家族支援・関係機関連携加算',
            desc: '学校・医療・就職先との連携書類の作成から保存までを一元管理。煩雑になりがちな外部連携業務もスマートにこなせます。',
            icon: <TeamOutlined />,
        },
        {
            title: '実地指導・行政監査への備え',
            desc: (
                <>
                    アセスメントに基づく一貫したロジックで支援計画書を生成。根拠の薄さを指摘されがちな<Highlight>行政の実地指導</Highlight>でも、慌てず対応できます。
                </>
            ),
            icon: <AuditOutlined />,
        }
    ];

    return (
        <Box
            component="section" // SEO: 加算支援セクションであることを定義
            sx={{
                py: SECTION_PY,
                bgcolor: '#f4f7f7', 
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Container>
                <Grid container spacing={isMobile ? 6 : 0} alignItems="center">

                    {/* 左側：ビジュアル・メッセージエリア */}
                    <Grid item xs={12} md={5}>
                        <Box sx={{ position: 'relative', pr: { md: 8 } }}>
                            <motion.div
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 1.2, delay: 1.7, ease: "easeOut" }}
                                aria-hidden="true" // 装飾要素
                                style={{
                                    position: 'absolute',
                                    top: -100,
                                    left: -200,
                                    zIndex: 0,
                                    pointerEvents: 'none'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 400,
                                        height: 400,
                                        background: `radial-gradient(circle, ${alpha(primaryColor, isMobile ? 0.3 : 0.4)} 0%, rgba(255,255,255,0) 70%)`,
                                    }}
                                />
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.7 }}
                                style={{ position: 'relative', zIndex: 1 }}
                            >
                                <Typography
                                    variant="caption"
                                    component="span" // spanにして構造をシンプルに
                                    sx={{
                                        color: primaryColor,
                                        fontWeight: 900,
                                        letterSpacing: '0.2em',
                                        display: 'block',
                                        mb: 2
                                    }}
                                >
                                    STEP 06 &nbsp;/&nbsp; REVENUE
                                </Typography>
                                <Typography
                                    variant="h2"
                                    component="h2" // SEO: セクションの見出し
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: { xs: '2rem', md: '2.8rem' },
                                        lineHeight: 1.3,
                                        color: '#1a1a1a',
                                        mb: 4
                                    }}
                                >
                                    各種加算書類の<br />
                                    作成を<Box component="span" sx={{ color: primaryColor }}>サポート</Box>。
                                </Typography>
                            </motion.div>
                        </Box>
                    </Grid>

                    {/* 右側：加算カードリスト */}
                    <Grid item xs={12} md={7}>
                        <Stack spacing={3} component="ul" sx={{ p: 0, listStyle: 'none' }}> {/* リスト構造として定義 */}
                            {billingData.map((item, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                                >
                                    <Box
                                        component="li" // リストアイテム
                                        sx={{
                                            p: { xs: 3, md: 4 },
                                            borderRadius: '24px',
                                            bgcolor: '#fff',
                                            boxShadow: `0 4px 20px ${alpha('#000', 0.03)}`,
                                            border: '1px solid #eef2f2',
                                            display: 'flex',
                                            gap: 3,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateX(10px)',
                                                boxShadow: `0 12px 30px ${alpha(primaryColor, 0.1)}`,
                                                borderColor: alpha(primaryColor, 0.2)
                                            }
                                        }}
                                    >
                                        <Box
                                            aria-hidden="true" // アイコンは装飾
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: '16px',
                                                bgcolor: alpha(primaryColor, 0.08),
                                                color: primaryColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '1.5rem',
                                                flexShrink: 0
                                            }}
                                        >
                                            {item.icon}
                                        </Box>
                                        <Box component="article"> {/* 各加算内容を独立した記事要素に */}
                                            <Typography
                                                variant="h5"
                                                component="h3" // SEO: 加算項目名をH3見出しに
                                                sx={{
                                                    fontWeight: 800,
                                                    mb: 1,
                                                    fontSize: '1.2rem',
                                                    color: '#333',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                {item.title}
                                                <ArrowRightOutlined aria-hidden="true" style={{ fontSize: '0.9rem', color: alpha(primaryColor, 0.4) }} />
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                component="p" // 本文
                                                sx={{
                                                    color: '#666',
                                                    lineHeight: 1.7,
                                                    fontSize: '0.95rem'
                                                }}
                                            >
                                                {item.desc}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            ))}
                        </Stack>
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
};

export default BillingSupportSection;